// Form submission abstraction.
// Routes to HubSpot Forms API when both REACT_APP_HUBSPOT_PORTAL_ID and the form-specific ID exist.
// Always mirrors to the existing MongoDB backend as a failsafe.

import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";

const HS_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID;
const FORM_IDS = {
  provider: process.env.REACT_APP_HUBSPOT_PROVIDER_FORM_ID,
  recruiting: process.env.REACT_APP_HUBSPOT_RECRUITING_FORM_ID,
  newsletter: process.env.REACT_APP_HUBSPOT_NEWSLETTER_FORM_ID,
};

const EVENT_NAMES = {
  provider: "lead_form_submit",
  recruiting: "career_form_submit",
  newsletter: "newsletter_subscribe",
};

// Map a payload to HubSpot Forms API field shape
function toHubSpotFields(payload) {
  return Object.entries(payload)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([name, value]) => ({ name, value: Array.isArray(value) ? value.join(";") : String(value) }));
}

async function submitToHubSpot(formType, payload, context = {}) {
  const formId = FORM_IDS[formType];
  if (!HS_PORTAL_ID || !formId) return { skipped: true };
  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${formId}`;
  const body = {
    fields: toHubSpotFields(payload),
    context: {
      pageUri: typeof window !== "undefined" ? window.location.href : undefined,
      pageName: typeof document !== "undefined" ? document.title : undefined,
      hutk: typeof document !== "undefined" ? readCookie("hubspotutk") : undefined,
      ...context,
    },
  };
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`HubSpot ${r.status}`);
  return r.json();
}

function readCookie(name) {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[1]) : undefined;
}

const MIRROR_ENDPOINTS = {
  provider: "/leads",
  recruiting: "/careers",
  newsletter: "/newsletter",
};

function toMirrorPayload(formType, p) {
  const attribution = {
    utm_source: p.utm_source,
    utm_medium: p.utm_medium,
    utm_campaign: p.utm_campaign,
    utm_term: p.utm_term,
    utm_content: p.utm_content,
    referrer: p.referrer,
  };
  if (formType === "newsletter") return { email: p.email, source: p.source_page || "footer", ...attribution };
  if (formType === "recruiting") {
    return {
      name: `${p.firstname || ""} ${p.lastname || ""}`.trim() || p.name,
      email: p.email,
      phone: p.phone,
      territory: p.target_territory || p.territory,
      experience_years: Number(p.experience_years) || 0,
      linkedin: p.linkedin_url || p.linkedin,
      cover_note: p.cover_note,
      source: p.source_page || "careers",
      ...attribution,
    };
  }
  // provider
  return {
    name: `${p.firstname || ""} ${p.lastname || ""}`.trim() || p.name,
    practice_name: p.practice_name,
    specialty: p.specialty,
    email: p.email,
    phone: p.phone,
    state: p.state,
    message: p.message,
    source: p.source_page || p.source || "homepage",
    ...attribution,
  };
}

export async function submitForm(formType, payload, eventParams = {}) {
  const event = EVENT_NAMES[formType];
  // Always merge in UTM + referrer attribution
  const attribution = getAttribution();
  payload = { ...attribution, ...payload };
  let hubspotResult = null;
  let mirrorResult = null;
  let hubspotErr = null;
  let mirrorErr = null;

  // Fire to HubSpot (best effort)
  try { hubspotResult = await submitToHubSpot(formType, payload); }
  catch (e) { hubspotErr = e; }

  // Always mirror to backend (failsafe + admin view)
  try {
    const ep = MIRROR_ENDPOINTS[formType];
    const mirror = toMirrorPayload(formType, payload);
    const r = await api.post(ep, mirror);
    mirrorResult = r.data;
  } catch (e) {
    mirrorErr = e;
  }

  // Both failing = real failure
  if (hubspotErr && mirrorErr) throw mirrorErr;

  // Fire analytics
  if (event) trackEvent(event, { form_type: formType, ...eventParams });

  return { hubspot: hubspotResult, mirror: mirrorResult, hubspotErr, mirrorErr };
}

export function hubspotEnabled(formType) {
  return Boolean(HS_PORTAL_ID && FORM_IDS[formType]);
}
